<template>
  <div
    class="icon-container"
    :class="{
      'has-two-gifts': showIconList.length === 2,
      'has-three-gifts': shouldEnable3d,
      'on-pay': hadLoadPayType
    }"
    :style="getGiftBackgroundStyle"
  >
    <!-- Safari 不挂载 3D 舞台，避免 preserve-3d + 无限旋转打爆 WebProcess -->
    <div
      v-if="canUse3dStage"
      class="stage-3d"
      :class="{ 'stage-active': shouldEnable3d }"
    >
      <div class="carousel-3d" :class="{ 'animation-paused': !shouldEnable3d }">
        <div
          v-for="(gift, index) in showIconList"
          :key="'3d-' + index"
          class="carousel-item"
          :class="`item-${index}`"
        >
          <img
            v-if="getGameIcon(gift)"
            class="icon-3d"
            :src="getGameIcon(gift)"
            alt="icon"
          />
        </div>
      </div>
    </div>

    <!-- 静态显示（不满足3D条件时） -->
    <template v-for="(gift, index) in showIconList">
      <img
        v-if="getGameIcon(gift)"
        :key="'static-' + index"
        class="icon"
        :class="`gift-icon-${index}`"
        :src="getGameIcon(gift)"
        alt="icon"
      />
    </template>
  </div>
</template>

<script>
import themeMixins from '@mixins/officialTopupNew/themeMixins';
import icon from '@mixins/officialTopupNew/icon';
import { isSafari } from '@utiles/officialStore/device';

export default {
  props: {
    background: {
      type: String,
      default: ''
    },
    gifts: {
      type: Array,
      default: () => []
    },
    enable3d: {
      type: Boolean,
      default: false
    },
    hadLoadPayType: {
      type: Boolean,
      default: false
    }
  },
  mixins: [themeMixins, icon],
  computed: {
    showIconList() {
      // 最多展示3个
      return this.gifts.slice(0, 3);
    },
    getGiftBackgroundStyle() {
      return {
        backgroundImage: `url(${this.background})`
      };
    },
    hasThreeGifts() {
      // 判断是否有3张图片
      return this.showIconList.length >= 3;
    },
    canUse3dStage() {
      return this.hasThreeGifts && !isSafari();
    },
    shouldEnable3d() {
      // 同时满足：支持3D舞台 + enable3d为true
      return this.canUse3dStage && this.enable3d;
    }
  },
  methods: {}
};
</script>
<style lang="scss" scoped>
.icon-container {
  box-sizing: border-box;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 204px;
  // padding-top: 30px;

  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 10px 10px 0 0;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  user-select: none;

  // 3D舞台始终存在，通过状态类控制显示
  .stage-3d {
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    width: 160px;
    height: 160px;
    transform-style: preserve-3d;
    opacity: 0;
    transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94),
      opacity 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    pointer-events: none;
    z-index: 10; // 确保3D舞台在上层

    // 阻止移动端长按出现系统菜单
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    // 激活状态
    &.stage-active {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
      pointer-events: auto;
      transition-delay: 0.05s; // 轻微延迟显示3D舞台
    }

    // 非激活状态时立即隐藏，无延迟
    &:not(.stage-active) {
      transition-delay: 0s;
    }
  }

  .carousel-3d {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;

    // 阻止移动端长按出现系统菜单
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;

    // 默认状态下禁用动画并重置到初始状态
    &.animation-paused {
      animation: none; // 完全禁用动画而不是暂停
      transform: rotateY(0deg); // 重置舞台到初始角度

      // 重置所有图片到正确的初始状态
      .carousel-item {
        // 保持3D位置但重置图片朝向
        &.item-0 .icon-3d {
          animation: none;
          transform: rotateY(0deg);
        }

        &.item-1 .icon-3d {
          animation: none;
          transform: rotateY(-120deg); // 补偿item-1的120度偏移
        }

        &.item-2 .icon-3d {
          animation: none;
          transform: rotateY(-240deg); // 补偿item-2的240度偏移
        }
      }
    }

    // RTL布局时反转旋转方向
    [dir='rtl'] & {
      &:not(.animation-paused) {
        animation-name: stage-rotate-rtl;
      }
    }
  }

  &.has-two-gifts {
    .gift-icon-0 {
      transform: translateX(-50%);
    }
    .gift-icon-1 {
      transform: translateX(50%);
    }
  }

  // 3D舞台效果
  &.has-three-gifts {
    perspective: 1000px;
    perspective-origin: center center;

    .carousel-3d {
      animation: stage-rotate 8s infinite linear;
    }

    // 3D模式时隐藏静态图标，使用延迟确保平滑切换
    .icon {
      opacity: 0;
      transform: scale(0);
      pointer-events: none;
      transition-delay: 0s; // 立即隐藏静态图标
    }
  }

  // 非3D模式时，静态图标的显示处理
  &:not(.has-three-gifts) {
    .icon {
      transition-delay: 0.15s; // 延迟显示静态图标，确保3D舞台完全消失
    }
  }
}

.icon {
  position: absolute;
  width: 130px;
  height: 130px;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    opacity 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: 1;
  transform-origin: center center;
  z-index: 5; // 静态图标在下层

  // 静态模式下简洁样式
  border-radius: 12px;
  object-fit: cover;
  // 不显示阴影效果
}

// 原始静态布局（默认状态）
@for $i from 0 through 5 {
  .gift-icon-#{$i} {
    z-index: 5-$i;

    @if $i %2 == 0 {
      transform: translateX(#{$i * 25%});
    } @else {
      transform: translateX(-#{$i * 25% + 25%});
    }
  }
}

// 3D舞台旋转动画 - 每个图片都需要反向旋转来保持正面
@keyframes stage-rotate {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}

// RTL布局的3D舞台旋转动画
@keyframes stage-rotate-rtl {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(-360deg);
  }
}

// 图片反向旋转动画，确保始终正面朝向用户
@keyframes icon-counter-rotate {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(-360deg);
  }
}

// item-1 的补偿旋转动画（抵消120度偏移）
@keyframes icon-counter-rotate-120 {
  0% {
    transform: rotateY(-120deg);
  }
  100% {
    transform: rotateY(-480deg); // -120 + (-360)
  }
}

// item-2 的补偿旋转动画（抵消240度偏移）
@keyframes icon-counter-rotate-240 {
  0% {
    transform: rotateY(-240deg);
  }
  100% {
    transform: rotateY(-600deg); // -240 + (-360)
  }
}

// 3D轮播项目设置
.carousel-item {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 130px;
  height: 130px;
  margin: -65px 0 0 -65px;
  transform-style: preserve-3d;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  // RTL布局时反转item的位置
  [dir='rtl'] & {
    &.item-0 {
      transform: rotateY(0deg) translateZ(80px);
      .icon-3d {
        animation: icon-counter-rotate-rtl 8s infinite linear;
      }
    }

    &.item-1 {
      transform: rotateY(-120deg) translateZ(80px);
      .icon-3d {
        animation: icon-counter-rotate-120-rtl 8s infinite linear;
      }
    }

    &.item-2 {
      transform: rotateY(-240deg) translateZ(80px);
      .icon-3d {
        animation: icon-counter-rotate-240-rtl 8s infinite linear;
      }
    }
  }
}

.icon-3d {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: all 0.2s ease;
  object-fit: cover;
  transform-style: preserve-3d;
  transform-origin: center center;
  // 确保图片平面完全垂直于视线
  backface-visibility: visible;
  pointer-events: none;

  // 阻止移动端长按出现系统菜单
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover {
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);
  }
}

// 3个图标的3D位置（120度间隔环绕，优化正面展示）
.item-0 {
  transform: rotateY(0deg) translateZ(80px);
  z-index: 3;

  .icon-3d {
    // item-0 不需要额外补偿，只需反向旋转抵消舞台旋转
    animation: icon-counter-rotate 8s infinite linear;
  }
}

.item-1 {
  transform: rotateY(120deg) translateZ(80px);
  z-index: 2;

  .icon-3d {
    // item-1 需要补偿120度偏移 + 反向旋转抵消舞台旋转
    animation: icon-counter-rotate-120 8s infinite linear;
  }
}

.item-2 {
  transform: rotateY(240deg) translateZ(80px);
  z-index: 1;

  .icon-3d {
    // item-2 需要补偿240度偏移 + 反向旋转抵消舞台旋转
    animation: icon-counter-rotate-240 8s infinite linear;
  }
}

// 移除高亮动画，保持简洁的均匀旋转效果
@media screen and (min-aspect-ratio: 1.2/1) {
  // #ssr-page.landscape {
  .icon-container {
    height: 194px;
    // padding-top: 40px;
  }

  .icon {
    width: 110px;
    height: 110px;

    // 横屏时静态模式也不显示阴影
    border-radius: 12px;
    object-fit: cover;
    // 不显示阴影效果
  }

  // 横屏时的静态图标显示时序
  .icon-container:not(.has-three-gifts) {
    .icon {
      transition-delay: 0.15s; // 延迟显示静态图标，确保3D舞台完全消失
    }
  }

  // 横屏时3D舞台基础样式
  .stage-3d {
    width: 140px;
    height: 140px;
    z-index: 10; // 确保横屏时也在上层

    // 横屏时的激活状态保持一致
    &.stage-active {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
      pointer-events: auto;
      transition-delay: 0.05s; // 轻微延迟显示3D舞台
    }

    // 横屏时非激活状态也立即隐藏
    &:not(.stage-active) {
      transition-delay: 0s;
    }
  }

  .carousel-item {
    width: 110px;
    height: 110px;
    margin: -55px 0 0 -55px;
  }

  // 横屏时的3D位置调整
  .has-three-gifts {
    .item-0 {
      transform: rotateY(0deg) translateZ(70px);

      .icon-3d {
        animation: icon-counter-rotate 8s infinite linear;
      }
    }

    .item-1 {
      transform: rotateY(120deg) translateZ(70px);

      .icon-3d {
        animation: icon-counter-rotate-120 8s infinite linear;
      }
    }

    .item-2 {
      transform: rotateY(240deg) translateZ(70px);

      .icon-3d {
        animation: icon-counter-rotate-240 8s infinite linear;
      }
    }

    // 横屏时的动画暂停状态重置
    .carousel-3d.animation-paused {
      .carousel-item {
        &.item-0 .icon-3d {
          animation: none;
          transform: rotateY(0deg);
        }

        &.item-1 .icon-3d {
          animation: none;
          transform: rotateY(-120deg);
        }

        &.item-2 .icon-3d {
          animation: none;
          transform: rotateY(-240deg);
        }
      }
    }
  }
}

// RTL布局的反向旋转动画
@keyframes icon-counter-rotate-rtl {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}

@keyframes icon-counter-rotate-120-rtl {
  0% {
    transform: rotateY(120deg);
  }
  100% {
    transform: rotateY(480deg);
  }
}

@keyframes icon-counter-rotate-240-rtl {
  0% {
    transform: rotateY(240deg);
  }
  100% {
    transform: rotateY(600deg);
  }
}
</style>
